/**
 *  < axios custom hook >
 *  Created at : 2024. 8. 20.
 *  Last edited at : 2024. 8. 20.
 *  Author : Kwonwoo Lyu
 **/

import React, {useState} from 'react';
import {useNavigate} from "react-router-dom";
import axios from "axios";
import UserService from "../services/UserService";
const useAxios = () => {
    const [isAxiosLoading, setIsAxiosLoading] = useState(false);
    const navigate = useNavigate();

    /** Global configuration */
    const _axiosConfig = {
        baseURL: `${process.env.REACT_APP_IMS_SERVER_URL}:${process.env.REACT_APP_IMS_SERVER_PORT}/api/v1`,
        timeout: 5000,
        headers: {
            'Content-Type' : 'application/json'
        }
    };

    /** Instantiation */
    const axiosInstance = axios.create(_axiosConfig);

    /** Interceptor configuration */

    /** for request */
    axiosInstance.interceptors.request.use(
        (config) => {
            const authConfig = {...config};
            /** authorize the request */
            authConfig.headers.Authorization = `Bearer ${sessionStorage.getItem("ims_accessToken")}`;
            return authConfig;
        },
        (error) => {
            /* preprocess error */
            console.error("axios instantiation config error");
            return Promise.reject(error);
        });

    /** for response */
    axiosInstance.interceptors.response.use(
        (response) => {
            return response;
        },
        (error) => {
            const httpStatusCode = error.response?.status;
            /** error handling */
            if (httpStatusCode === 404) {
                console.log(error);
                /** not found */
                navigate('/404');
            } else if (httpStatusCode === 401) {
                console.log(error);
                /** unauthorized */
                navigate('/401');
            } else if (httpStatusCode != null) {
                console.log(error);
                /** server-side error */
                /* for error propagation */
            } else {
                /** unknown error */
                /** service unavailable */
                console.log(error);
            }
            /* for error propagation */
            return Promise.reject(error);
        }
    );

    /** private methods */
    const _validatePathAndParams = (path, pathParams) => {
        /* pattern extraction */
        const pathVars = [...path.matchAll(/:(\d+)/g)].map(match => parseInt(match[1]));

        /* check path ordering */
        for (let i = 0; i < pathVars.length; i++) {
            if (pathVars[i] !== i + 1) {
                throw new Error(`Path variables are not in correct order: expected :${i + 1} but found :${pathVars[i]}`);
            }
        }

        /* check length */
        if (pathVars.length !== pathParams.length) {
            throw new Error(`Number of path variables (${pathVars.length}) does not match the number of provided parameters (${pathParams.length})`);
        }
    };

    const _buildUrl = (path, pathParams) => {
        if (pathParams.length === 0) {
            return path;
        }

        /** validation */
        _validatePathAndParams(path, pathParams);

        /** build */
        return path.replace(/:\d+/g, (match) => {
            const index = parseInt(match.substring(1)) - 1;
            return encodeURIComponent(pathParams[index]);
        });
    };

    /** hook method */

    /**
     * Custom hook method for the HTTP request using axios instance.
     * This method updates the JWT Token and send the request.
     *  @param {string} methodType HTTP request type. ("GET", "PUT", "POST", or "DELETE")
     *  @param {string} url [required] HTTP request url, the path variables must be given with number with valid pattern('/:(\d+)/g'). e.g. :1, :2, ...
     *  @param {array} pathParams Path variables in order.
     *  @param {object} requestBody JSON formatted HTTP request body, only for PUT and POST method.
     *  @param {FormData} formData FormData formatted HTTP request body, only for "multipart/form-data".
     *  @param {object} queryParams HTTP request query parameters, key-value object format.
     *  @param {string} contentType HTTP request "Content-Type" header, must be in "default"(application/json) or "multipart/form-data".
     *  @returns {Promise<object>>} The request body wrapped by a fulfilled Promise object.
     *  @returns {Promise<object>>} The thrown error object wrapped by a rejected Promise object.
     **/
    const axiosCall = async (methodType = "GET", url, pathParams = [], requestBody = {}, formData, queryParams = {}, contentType = "default") => {
        /** check method */
        const validMethods = ["GET", "PUT", "POST", "DELETE"];
        const validMethodsWithBody = ["PUT", "POST"];
        if (!validMethods.includes(methodType)) {
            throw new Error(`The given method type is not supported. Expected "GET", "PUT", "POST", or "DELETE", but got ${methodType}.`);
        }

        /** build url */
        const requestUrl = _buildUrl(url, pathParams);

        /** generate config */
        const _config = {..._axiosConfig};

        /* method */
        _config['method'] = methodType.toLowerCase();

        /* url */
        _config['url'] = requestUrl;

        /* header and body */
        if (contentType === "multipart/form-data") {
            if (!validMethodsWithBody.includes(methodType)) {
                throw new Error(`The given method type is not supported. Expected "PUT" or "POST", but got ${methodType}.`);
            }
            /* override to the global configuration */
            _config.headers["Content-Type"] = contentType;
            _config.data = formData;
        } else {
            /* application/json */
            if (validMethodsWithBody.includes(methodType)) {
                _config.data = JSON.stringify(requestBody);
            }
        }

        /* query parameters */
        let queryParamKeys = Object.keys(queryParams)
        if (queryParamKeys.length > 0) {
            _config.params = queryParams;
        }

        /** send request */
        try {
            await setIsAxiosLoading(true);
            const response = await UserService.updateToken(() => axiosInstance.request(_config));
            return response;
        } finally {
            await setIsAxiosLoading(false);
        }
    };

    return { isAxiosLoading, axiosCall };
};

export default useAxios;
